<template>
    <section class="dp-layout league-affair-config-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right">
            <template v-if="schoolId > 0">
                <div class="header-bar">
                    <div class="school-info">
                        <span class="label">当前学校：</span>
                        <span class="value">{{ schoolName || '未命名学校' }}</span>
                    </div>
                </div>

                <el-alert
                    title="请先在「值日检查 - 检查项目」中配置加分项，再按校区关联。启用后，学生刷脸签到成功即可为本班本月加一次分（首签触发），无需审批。"
                    type="info"
                    :closable="false"
                    show-icon
                    class="tip-alert"
                />

                <el-table :data="configList" v-loading="loading" border style="width: 100%;" class="compact-table">
                    <el-table-column prop="CampusName" label="校区" min-width="140" show-overflow-tooltip />
                    <el-table-column label="启用" width="100" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.Enabled ? 'success' : 'info'" size="mini">
                                {{ scope.row.Enabled ? '已启用' : '未启用' }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="关联加分项" min-width="260" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span v-if="scope.row.DutyInspectItemInvalid" class="warn-text">配置已失效，请重新选择</span>
                            <span v-else-if="scope.row.DutyInspectItemPath">{{ scope.row.DutyInspectItemPath }}</span>
                            <span v-else-if="scope.row.DutyInspectItemName">{{ scope.row.DutyInspectItemName }}</span>
                            <span v-else class="muted">未配置</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="分值" width="90" align="center">
                        <template slot-scope="scope">
                            <span v-if="scope.row.DutyInspectItemScore != null && !scope.row.DutyInspectItemInvalid">
                                +{{ formatScore(scope.row.DutyInspectItemScore) }}
                            </span>
                            <span v-else class="muted">-</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="120" align="center" fixed="right">
                        <template slot-scope="scope">
                            <el-button
                                v-if="hasActionPermission('openEditDialog')"
                                type="primary"
                                size="mini"
                                @click="openEditDialog(scope.row)"
                            >配置</el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧选择所属学校，配置各校区团务考核</p>
                </div>
            </div>
        </section>

        <el-dialog
            title="团务考核配置"
            :visible.sync="dialogVisible"
            width="520px"
            :close-on-click-modal="false"
            @closed="onDialogClosed"
        >
            <el-form ref="editForm" :model="editForm" :rules="editRules" label-width="120px" size="small">
                <el-form-item label="校区">
                    <span>{{ editForm.CampusName }}</span>
                </el-form-item>
                <el-form-item label="启用">
                    <el-switch v-model="editForm.Enabled" active-text="开启" inactive-text="关闭" @change="onEnabledChange" />
                </el-form-item>
                <el-form-item label="关联加分项" prop="DutyInspectItemId">
                    <el-cascader
                        v-model="editForm.DutyInspectItemId"
                        :options="itemTree"
                        :props="cascaderProps"
                        :show-all-levels="true"
                        clearable
                        filterable
                        placeholder="请选择加分项"
                        style="width: 100%;"
                        :disabled="treeLoading"
                        @change="onItemChange"
                    />
                </el-form-item>
                <el-form-item v-if="selectedItemScore != null" label="加分分值">
                    <span class="score-text">+{{ formatScore(selectedItemScore) }} 分</span>
                    <span class="score-hint">（在「检查项目」中维护）</span>
                </el-form-item>
            </el-form>
            <div slot="footer">
                <el-button size="small" @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="handleSave">保存</el-button>
            </div>
        </el-dialog>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import {
    getLeagueAffairConfigList,
    getLeagueAffairAdditionItemTree,
    saveLeagueAffairConfig
} from '../../api/modules/leagueAffair';
import { normalizeDeptId, deptIdsEqual, resolveActiveSchoolId } from '../../utils/deptCascaderProps';

export default {
    name: 'LeagueAffairConfig',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            buttonList: [],
            loading: false,
            saving: false,
            treeLoading: false,
            configList: [],
            dialogVisible: false,
            itemTree: [],
            editForm: {
                CampusId: 0,
                CampusName: '',
                Enabled: false,
                DutyInspectItemId: null
            },
            selectedItemScore: null,
            editRules: {
                DutyInspectItemId: [{
                    validator: (rule, value, callback) => {
                        if (this.editForm.Enabled && !value) {
                            callback(new Error('启用时必须选择加分项'));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'change'
                }]
            },
            cascaderProps: {
                value: 'id',
                label: 'name',
                children: 'children',
                emitPath: false
            }
        };
    },
    created() {
        this.buttonList = getButtonList(this.$route.path) || [];
        const sid = this.resolveSchoolId();
        if (sid) {
            this.applySchoolChange(sid, this.$store.state.activeSchoolName);
        }
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.applySchoolChange(newSchoolId, this.$store.state.activeSchoolName);
        }
    },
    methods: {
        /** 判断当前路由是否有指定 Func 按钮权限 */
        hasActionPermission(func) {
            if (!this.buttonList || !this.buttonList.length) return true;
            return this.buttonList.some(button => button.Func === func);
        },

        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },

        /** 切换学校并刷新配置列表 */
        applySchoolChange(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            if (deptIdsEqual(id, this.schoolId) && deptIdsEqual(this.boundSchoolId, id)) return;
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
            this.loadConfigList();
        },

        treeNodeClick(data) {
            this.applySchoolChange(data && data.value, data && data.label);
        },

        formatScore(v) {
            const n = Number(v);
            if (Number.isNaN(n)) return '-';
            return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
        },

        /** 归一化加分项树（兼容大小写字段） */
        normalizeTree(nodes) {
            if (!Array.isArray(nodes)) return [];
            return nodes.map(n => {
                const childrenRaw = n.children || n.Children;
                const nodeType = n.nodeType || n.NodeType;
                const children = this.normalizeTree(childrenRaw);
                const isLeaf = nodeType === 'item' || !children.length;
                return {
                    id: String(n.id != null ? n.id : n.Id),
                    name: n.name != null ? n.name : n.Name,
                    scoreValue: n.scoreValue != null ? n.scoreValue : n.ScoreValue,
                    children: isLeaf ? undefined : children
                };
            });
        },

        /** 加载各校区团务考核配置 */
        async loadConfigList() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                const res = await getLeagueAffairConfigList({ schoolId: this.schoolId });
                if (res.data && res.data.success) {
                    this.configList = res.data.response || [];
                } else {
                    this.configList = [];
                    this.$message.error((res.data && res.data.msg) || '加载配置失败');
                }
            } catch (e) {
                this.configList = [];
                this.$message.error('加载配置失败');
            } finally {
                this.loading = false;
            }
        },

        /** 打开校区配置弹窗并加载加分项树 */
        async openEditDialog(row) {
            this.editForm = {
                CampusId: row.CampusId,
                CampusName: row.CampusName,
                Enabled: !!row.Enabled,
                DutyInspectItemId: row.DutyInspectItemId ? String(row.DutyInspectItemId) : null
            };
            this.selectedItemScore = row.DutyInspectItemInvalid
                ? null
                : (row.DutyInspectItemScore != null ? row.DutyInspectItemScore : null);
            this.dialogVisible = true;
            this.treeLoading = true;
            try {
                const res = await getLeagueAffairAdditionItemTree({
                    schoolId: this.schoolId,
                    campusId: row.CampusId || 0
                });
                if (res.data && res.data.success) {
                    this.itemTree = this.normalizeTree(res.data.response || []);
                    this.syncSelectedScore();
                } else {
                    this.itemTree = [];
                    this.$message.error((res.data && res.data.msg) || '加载加分项失败');
                }
            } catch (e) {
                this.itemTree = [];
                this.$message.error('加载加分项失败');
            } finally {
                this.treeLoading = false;
            }
        },

        findLeafScore(nodes, id) {
            if (!id || !Array.isArray(nodes)) return null;
            const key = String(id);
            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
                if (String(n.id) === key && (!n.children || !n.children.length)) {
                    return n.scoreValue != null ? n.scoreValue : null;
                }
                const hit = this.findLeafScore(n.children, id);
                if (hit != null) return hit;
            }
            return null;
        },

        syncSelectedScore() {
            if (!this.editForm.DutyInspectItemId) {
                this.selectedItemScore = null;
                return;
            }
            const score = this.findLeafScore(this.itemTree, this.editForm.DutyInspectItemId);
            this.selectedItemScore = score != null ? score : this.selectedItemScore;
        },

        onItemChange() {
            this.syncSelectedScore();
            this.$nextTick(() => this.$refs.editForm && this.$refs.editForm.clearValidate());
        },

        onEnabledChange() {
            this.$nextTick(() => this.$refs.editForm && this.$refs.editForm.clearValidate());
        },

        onDialogClosed() {
            this.itemTree = [];
            this.selectedItemScore = null;
            this.$refs.editForm && this.$refs.editForm.clearValidate();
        },

        /** 保存团务考核校区配置 */
        handleSave() {
            this.$refs.editForm.validate(async valid => {
                if (!valid) return;
                this.saving = true;
                try {
                    const payload = {
                        schoolId: this.schoolId,
                        campusId: this.editForm.CampusId,
                        enabled: this.editForm.Enabled,
                        dutyInspectItemId: this.editForm.DutyInspectItemId
                            ? Number(this.editForm.DutyInspectItemId)
                            : null
                    };
                    const res = await saveLeagueAffairConfig(payload);
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '保存成功');
                        this.dialogVisible = false;
                        this.loadConfigList();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.saving = false;
                }
            });
        }
    }
};
</script>

<style scoped>
.league-affair-config-page .header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}
.league-affair-config-page .school-info .label {
    color: #909399;
}
.league-affair-config-page .school-info .value {
    font-weight: 600;
    color: #303133;
}
.league-affair-config-page .tip-alert {
    margin-bottom: 12px;
}
.league-affair-config-page .warn-text {
    color: #E6A23C;
}
.league-affair-config-page .muted {
    color: #C0C4CC;
}
.league-affair-config-page .score-text {
    color: #67C23A;
    font-weight: 600;
}
.league-affair-config-page .score-hint {
    margin-left: 6px;
    color: #C0C4CC;
    font-size: 12px;
}
.no-department-tip {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.no-department-tip__inner {
    text-align: center;
    color: #909399;
}
.no-department-tip__inner i {
    font-size: 40px;
    margin-bottom: 12px;
}
</style>
